<template>
  <v-container fluid>
    <v-row justify="start">
      <v-col>
        <h1>TBot Prescreen Prospect Generator</h1>
        <p>Fill out the form sections below to generate a new Prescreen Prospect.</p>
      </v-col>
      <v-col
        v-if="!dataLoading && !showError"
        class="d-flex align-end justify-end"
      >
        <v-btn
          id="send_prospect_btn"
          color="primary"
          class="submit-btn mb-4"
          :loading="sendingProspect"
          @click="submitProspect"
        >
          Submit Prospect
        </v-btn>
      </v-col>
    </v-row>

    <template
      v-if="dataLoading"
    >
      <p>Loading data</p>
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      />
    </template>

    <v-row
      v-else
    >
      <v-col
        v-if="!showError"
      >
        <v-card
          class="my-2"
          elevation="10"
        >
          <v-card-title>
            Campaign
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                cols="3"
                md="3"
                sm="3"
              >
                <v-text-field
                  id="campaign_name"
                  v-model="campaign.name"
                  label="Name"
                />
              </v-col>
              <v-col
                cols="3"
                md="3"
                sm="3"
              >
                <v-select
                  id="lead_provider_id"
                  v-model="campaign.leadProviderId"
                  :items="leadProviders"
                  item-title="text"
                  item-value="value"
                  label="Lead Provider"
                />
              </v-col>
              <v-col
                cols="3"
                md="3"
                sm="3"
              >
                <v-text-field
                  id="campaign_code"
                  v-model="campaign.campaignCode"
                  label="Campaign Code"
                />
              </v-col>
              <v-col
                cols="3"
                md="3"
                sm="3"
              >
                <v-text-field
                  id="max_loan_amount"
                  v-model="campaign.maxLoanAmount"
                  label="Max Loan Amount"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="4"
                md="4"
                sm="4"
              >
                <v-text-field
                  id="print_expire_date"
                  v-model="campaign.printExpireDate"
                  label="Print Expire Date"
                  type="date"
                />
              </v-col>
              <v-col
                cols="4"
                md="4"
                sm="4"
              >
                <v-text-field
                  id="system_expire_date"
                  v-model="prospect.systemExpireDate"
                  label="System Expire Date"
                  type="date"
                />
              </v-col>

              <v-col
                cols="4"
                md="4"
                sm="4"
              >
                <v-text-field
                  id="active_date"
                  v-model="campaign.activeDate"
                  label="Active Date"
                  type="date"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="6"
                md="6"
                sm="6"
              >
                <v-text-field
                  id="mailed_date"
                  v-model="campaign.mailedDate"
                  label="Mailed Date"
                  type="date"
                />
              </v-col>

              <v-col
                cols="6"
                md="6"
                sm="6"
              >
                <v-select
                  id="external_campaign"
                  v-model="campaign.externalCampaignId"
                  :items="campaigns"
                  item-title="text"
                  item-value="value"
                  label="External Campaign"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-card
      class="my-2"
      elevation="10"
    >
      <v-card-title>
        Prospect
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="3"
            md="3"
            sm="3"
          >
            <v-checkbox
              id="refinance_loan"
              v-model="prospect.isTransUnion"
              label="Use TransUnion"
            />
          </v-col>
          <v-col
            cols="3"
            md="3"
            sm="3"
          >
            <v-text-field
              id="offer_code"
              v-model="prospect.offerCode"
              label="Offer Code"
              type="text"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="3"
            md="3"
            sm="3"
          >
            <v-text-field
              id="first_name"
              v-model="prospect.firstName"
              label="First Name"
              type="text"
            />
          </v-col>

          <v-col
            cols="2"
            md="2"
            sm="2"
          >
            <v-text-field
              id="middle_initial"
              v-model="prospect.middleInitial"
              label="Middle Initial"
              type="text"
            />
          </v-col>
          <v-col
            cols="3"
            md="3"
            sm="3"
          >
            <v-text-field
              is="last_name"
              v-model="prospect.lastName"
              label="Last Name"
              type="text"
            />
          </v-col>
          <v-col
            cols="2"
            md="2"
            sm="2"
          >
            <v-text-field
              id="ssn_last_7"
              v-model="prospect.ssn7"
              label="SSN last 7"
              type="text"
            />
          </v-col>
          <v-col
            cols="2"
            md="2"
            sm="2"
          >
            <v-text-field
              id="prospect_suffix"
              v-model="prospect.suffix"
              label="Suffix"
              type="text"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="3"
            md="3"
            sm="3"
          >
            <v-text-field
              id="street_address"
              v-model="prospect.streetAddress"
              label="Address"
              type="text"
            />
          </v-col>
          <v-col
            cols="3"
            md="3"
            sm="3"
          >
            <v-text-field
              id="street_address_2"
              v-model="prospect.streetAddress2"
              label="Apt/Suite #"
              type="text"
            />
          </v-col>
          <v-col
            cols="2"
            md="2"
            sm="2"
          >
            <v-text-field
              id="city"
              v-model="prospect.city"
              label="City"
              type="text"
            />
          </v-col>
          <v-col
            cols="2"
            md="2"
            sm="2"
          >
            <v-text-field
              id="state"
              v-model="prospect.state"
              label="State"
              type="text"
            />
          </v-col>
          <v-col
            cols="2"
            md="2"
            sm="2"
          >
            <v-text-field
              id="zip"
              v-model="prospect.zip"
              label="Zip"
              type="text"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-col
      v-if="showError"
      cols="12"
    >
      <v-alert
        type="error"
        dark
        border="start"
      >
        Failed to retrieve data needed to create lead.
        <ul>
          <li
            v-for="(msg, index) in errorMessages"
            :key="index"
          >
            {{ msg }}
          </li>
        </ul>
      </v-alert>
    </v-col>
    <v-dialog
      v-model="showModal"
      @click:outside="handleCloseResult()"
    >
      <v-card
        flat
        class="pa-2"
      >
        <v-card-title>Result</v-card-title>
        <v-card-text>
          <v-alert
            v-show="formError"
            type="error"
            dark
            border="start"
          >
            {{ formError }}
          </v-alert>

          <vue-json-pretty
            id="json_api_response"
            :data="apiResponse"
            :show-line="false"
            :show-double-quotes="false"
            virtual
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PrescreenProspectIndex',

  components: {
    VueJsonPretty,
  },

  data() {
    return {
      dataLoading: false,
      showError: false,
      sendingProspect: false,
      errorMessages: [],
      apiResponse: {},
      showModal: false,
      formError: '',
      campaigns: [],
      leadProviders: [],
      campaignSelected: {},
      leadProviderSelected: {},
    };
  },

  computed: {
    ...mapGetters('prescreenProspect', ['campaign', 'prospect']),
  },

  created() {
    this.dataLoading = true;
    this.$axios.post('/api/v1/support/make-test-prospect/loadDataNeeded')
      .then((data) => {
        this.campaigns = data.data.campaigns.map((campaign) => ({
          value: campaign.id,
          text: `${campaign.id} (${campaign.campaign_name} - ${campaign.product_type})`,
        }));

        this.leadProviders = data.data.leadProviders.map((provider) => ({
          value: provider.id,
          text: `${provider.api_key} (${provider.category})`,
        }));
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          this.apiResponse = error.response.data;
          this.formError = 'Please fix the following errors';
        } else {
          this.setNotification({
            message: 'There was an error processing the prospect, please try again',
            color: 'red',
          });
          console.error(error);
        }
      })
      .finally(() => {
        this.dataLoading = false;
      });
  },

  methods: {
    ...mapActions('notification', ['setNotification']),
    ...mapActions('prescreenProspect', ['refreshData']),
    submitProspect() {
      this.sendingProspect = true;

      this.$axios.post('api/v1/support/make-test-prospect', {
        campaign: this.campaign,
        prospect: this.prospect,
      })
        .then((resp) => {
          if (resp.status === 200) {
            this.showModal = true;
            this.apiResponse = resp.data;
          } else {
            this.setNotification({
              message: 'There was an error processing the prospect, please try again',
              color: 'red',
            });
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.apiResponse = error.response.data;
            this.formError = 'Please fix the following errors';
            let message = 'There was an error processing the prospect, please try again';

            if (error.response.data.errors['campaign.campaignCode']) {
              message = 'Campaign code has already been taken. Try a new campaign code';
            }

            this.setNotification({
              message,
              color: 'red',
            });
          } else {
            this.setNotification({
              message: 'There was an error processing the prospect, please try again',
              color: 'red',
            });
            console.error(error);
          }
        })
        .finally(() => {
          this.sendingProspect = false;
        });
    },

    handleCloseResult() {
      this.refreshData();
    },

  },
};
</script>

<style scoped>
.submit-btn {
  background-color: #205e86 !important;
  color: white !important;
  font-weight: 700 !important;
}
</style>
